import i18next from 'i18next';

import { getEnvBoolean } from './ReactScriptHelper';
import { mediaQueryMatch } from './UserAgent';
import { PersistentStore } from './store/PersistentStore';

export type AppTheme = 'auto' | 'light' | 'dark';

const darkmodeMediaQuery: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

const SUPPORT_DARKMODE = getEnvBoolean('SUPPORT_DARKMODE', true);

export interface LayoutHandlerState {
    darkmode: boolean;
    layoutMode: AppTheme;
    smallLayout: boolean;
    phoneLandscape: boolean;
}

interface Breakpoints {
    sm: number;
    md: number;
}

class LayoutHandler extends PersistentStore<LayoutHandlerState> {
    private breakpoints: Breakpoints = {
        sm: 568,
        md: 960,
    };

    public get isDarkmodeActive(): boolean {
        const { layoutMode, darkmode } = this.snapshot;
        switch (layoutMode) {
            case 'auto':
                return darkmode;
            default:
                return layoutMode === 'dark';
        }
    }

    public get isPhone(): boolean {
        return this.snapshot.smallLayout || this.snapshot.phoneLandscape;
    }

    constructor() {
        super(
            'layout',
            {
                darkmode: SUPPORT_DARKMODE ? mediaQueryMatch('(prefers-color-scheme: dark)') : false,
                layoutMode: SUPPORT_DARKMODE ? 'auto' : 'light',
                smallLayout: false,
                phoneLandscape: false,
            },
            [],
            false,
            !SUPPORT_DARKMODE,
        );

        this.update = this.update.bind(this);
        this.useDarkmodeActive = this.useDarkmodeActive.bind(this);
        this.useBreakpointInfo = this.useBreakpointInfo.bind(this);
        this.update();

        try {
            window.addEventListener('resize', this.update);
            // Chrome & Firefox
            darkmodeMediaQuery.addEventListener('change', (event: MediaQueryListEvent) => {
                this.updateState({ darkmode: event.matches });
            });
        } catch {
            try {
                // Safari
                darkmodeMediaQuery.addListener((event: MediaQueryListEvent) => {
                    this.updateState({ darkmode: event.matches });
                });
            } catch {
                /* */
            }
        }
    }

    public useDarkmodeActive() {
        const { layoutMode, darkmode } = this.useState(['layoutMode', 'darkmode']);
        switch (layoutMode) {
            case 'auto':
                return darkmode;
            default:
                return layoutMode === 'dark';
        }
    }

    public useBreakpointInfo() {
        return this.useState(['phoneLandscape', 'smallLayout']);
    }

    public setBreakpoints(newBreakpoints: Breakpoints): void {
        this.breakpoints = newBreakpoints;
        this.update();
    }

    private update(): void {
        if (this.breakpoints == null) {
            return;
        }

        this.updateState({
            smallLayout: mediaQueryMatch(`(max-width:${this.breakpoints.sm - 0.05}px)`),
            phoneLandscape: mediaQueryMatch(
                `(max-width:${this.breakpoints.md - 0.05}px) and (max-height:${this.breakpoints.sm - 0.05}px) and (orientation:landscape)`
            ),
        });
    }
}

export const layoutInformation: LayoutHandler = new LayoutHandler();

export function layoutModeToLabel(layout: AppTheme | undefined): string {
    switch (layout) {
        case 'dark':
            return i18next.t('Dark');
        case 'light':
            return i18next.t('Light');
        default:
            return i18next.t('Automatic');
    }
}
